/**
 * `PreferenciasApi` com reserva local (PRF-02..PRF-07).
 *
 * O endpoint `/preferencias/tabelas` ainda não existe. Enquanto isso, a cópia
 * que sobrevive a fechar a aba é a do `localStorage`. No dia em que o servidor
 * subir, ele passa a ser a fonte sem nenhuma mudança no consumidor.
 *
 * - `listar`: servidor; o local só entra se o servidor rejeitar. Servidor que
 *   responde vence, sem mescla.
 * - `salvar`/`remover`: local primeiro, depois servidor. Falha só do servidor
 *   resolve e reporta no máximo uma vez por instância: sem backend, toda escrita
 *   falharia, e reportar cada uma inundaria o Sentry. Falha dos dois rejeita, e
 *   o retry de `useTabelaPreferencias` assume.
 *
 * Recebe as duas implementações por parâmetro, sem importar nada de `api/` ou
 * `libs/`.
 */
import { CHAVE_STORAGE_BASE, type PreferenciasApi } from "./useTabelaPreferencias";

export function criarPreferenciasApiComReserva(
  remota: PreferenciasApi,
  local: PreferenciasApi,
  aoFalharRemoto?: (erro: unknown) => void,
): PreferenciasApi {
  let reportado = false;

  /** Local primeiro, depois remoto. Só rejeita quando os dois falham. */
  async function escrever(
    noLocal: () => Promise<void>,
    noRemoto: () => Promise<void>,
  ): Promise<void> {
    let localGravou = true;
    try {
      await noLocal();
    } catch {
      localGravou = false;
    }
    try {
      await noRemoto();
    } catch (erro) {
      if (!localGravou) throw erro;
      if (reportado) return;
      reportado = true;
      aoFalharRemoto?.(erro);
    }
  }

  return {
    async listar() {
      try {
        return await remota.listar();
      } catch {
        return local.listar();
      }
    },
    salvar(tabelaId, pref) {
      return escrever(
        () => local.salvar(tabelaId, pref),
        () => remota.salvar(tabelaId, pref),
      );
    },
    remover(tabelaId) {
      return escrever(
        () => local.remover(tabelaId),
        () => remota.remover(tabelaId),
      );
    },
  };
}

/**
 * Esvazia o `localStorage` na troca de versão do Progali, menos a reserva de
 * preferências (`preferenciasTabelas:<email>`) (PRF-09, PRF-10). Sem isso, cada
 * deploy apagaria a única cópia da escolha de colunas enquanto o endpoint não
 * existir.
 *
 * SPEC_DEVIATION: mora aqui, e não em `useTabelaPreferencias.ts` como diz a T4.
 * Reason: o `gate:loc-hospedeiro` trava aquele arquivo em 266 linhas
 * (`.loc-hospedeiro-baseline.json`). O prefixo continua vindo de lá
 * (`CHAVE_STORAGE_BASE`), que segue dono do formato da chave.
 */
export function limparLocalStorageExcetoPreferencias(): void {
  try {
    const alvos = Object.keys(localStorage).filter(
      (chave) => !chave.startsWith(`${CHAVE_STORAGE_BASE}:`),
    );
    for (const chave of alvos) localStorage.removeItem(chave);
  } catch {
    // `localStorage` indisponível: a verificação de versão segue sem ele (PRF-11).
  }
}
